/**
 * RequireAuth · 路由守卫组件.
 *
 * 用法:
 *   <Route path="/cnc" element={<RequireAuth><CncLayout /></RequireAuth>}>
 *
 * 行为:
 *   - 加载中 → Spin loading
 *   - 未登录 → 跳 /login?from=<原路径>
 *   - 已登录 → 渲染 children
 *   - 可选 role 白名单 → 不够跳 /403
 */
import { Spin } from "antd";
import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import type { UserRole } from "@/api/auth";

interface RequireAuthProps {
  children: ReactNode;
  /** 允许的 role 列表 · 不设表示所有登录用户都行. */
  allowedRoles?: UserRole[];
}

export default function RequireAuth({
  children,
  allowedRoles,
}: RequireAuthProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin tip="加载中..." size="large">
          <div style={{ padding: 40 }} />
        </Spin>
      </div>
    );
  }

  if (!isAuthenticated) {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?from=${from}`} replace />;
  }

  // role 检查
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return (
      <Navigate
        to="/403"
        state={{
          deniedPath: location.pathname + location.search + location.hash,
        }}
        replace
      />
    );
  }

  return <>{children}</>;
}
